import { Grid, Paper } from '@mui/material';
import { EntitlementInterface } from '@thro-types/access-handler';

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { useEntitlementsQuery } from 'api/hooks/accessHandler';
import { useFacultiesQuery } from 'api/hooks/useFacultiesQuery';
import { TypedGridColumns } from 'app/components';
import { DataGrid } from 'app/components/DataGrid';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import usePageHeading from 'utils/hooks/usePageHeading';

import EntitlementGroupDetail from '../EntitlementGroups/EntitlementGroupDetail/EntitlementGroupDetail';

export function Entitlements() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const location = useLocation();

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0
  });
  usePageHeading({
    heading: t(translations.pages.entitlements.title()),
    headingProps: { back: location.state?.back ?? `/settings/access-handler` }
  });

  const { data: faculties } = useFacultiesQuery();

  const {
    data: entitlementsData,
    isLoading,
    isError
  } = useEntitlementsQuery({
    params: {
      limit: paginationModel.pageSize,
      offset: paginationModel.page * paginationModel.pageSize
    }
  });

  const handlePaginationChange = useCallback(pagination => {
    setPaginationModel(pagination);
  }, []);

  const columns = useMemo((): TypedGridColumns<EntitlementInterface> => {
    return [
      {
        field: 'id',
        flex: 1,
        headerName: t(translations.common.id()),
        align: 'center',
        headerAlign: 'center'
      },
      {
        field: 'name',
        flex: 4,
        headerName: t(translations.pages.entitlements.tableHeader.entitlement())
      },
      {
        field: 'entitlementGroup',
        flex: 8,
        headerName: t(translations.pages.entitlements.tableHeader.entitlementGroup()),
        renderCell: params =>
          params.row.entitlementGroup ? (
            <EntitlementGroupDetail
              entitlementGroup={params.row.entitlementGroup}
              faculties={faculties}
            />
          ) : null
      }
    ];
  }, [faculties, t]);

  if (isError) {
    dispatch(
      AlertActions.error(`${t(translations.pages.managePermissionsSettings.errorLoading())}`)
    );
    navigate('/', { replace: true });
    return;
  }

  return (
    <Paper>
      <Grid container style={{ height: '70vh' }}>
        <DataGrid
          style={{ height: '100%', width: '100%' }}
          disableColumnFilter
          disableRowSelectionOnClick
          disableColumnMenu
          columns={columns}
          rows={entitlementsData?.data || []}
          rowCount={entitlementsData?.pagination.count || 0}
          paginationMode="server"
          paginationModel={{
            page: paginationModel.page,
            pageSize: paginationModel.pageSize
          }}
          onPaginationModelChange={pagModel => {
            handlePaginationChange(pagModel);
          }}
          loading={isLoading}
        />
      </Grid>
    </Paper>
  );
}
